import Image from 'next/image';
import Link from 'next/link';
import HomepageHeader from '@/components/HomepageHeader';
import HomepageFooter from '@/components/HomepageFooter';

export default function VerificationStandards() {
  return (
    <div className="min-h-screen bg-[#F6EFED] page-transition">
      {/* Header Component */}
      <HomepageHeader />

      {/* Hero Section with Background */}
      <section className="relative h-[600px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/about-banner.jpg"
            alt="Verification Standards"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#4C3E581A]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1920px] mx-auto h-full flex items-center justify-center absolute bottom-0 left-0 right-0 pt-[180px]">
          <div className="max-w-[842px] text-center">
            <h1 className="font-['Gotham_Rounded',sans-serif] text-[64px] font-bold leading-[76px] tracking-[-4%] text-white mb-10" style={{ marginBottom: '10px' }}>
              Verification Standards
            </h1>
            <div className="flex items-center justify-center gap-2 font-['Archivo',sans-serif] text-[20px] text-white">
              <Link href="/" className="hover:opacity-80 transition-opacity">Home</Link>
              <span>/</span>
              <span className="text-[#8A7296]">Verification Standards</span>
            </div>
          </div>
        </div>
      </section>

      {/* First Section */}
      <section className="bg-[#f6efed] py-[100px]">
        <div className="max-w-[1280px] mx-auto px-[20px]">
          <div className="grid lg:grid-cols-2 gap-[100px] items-center">
            {/* Left - Image */}
            <div className="relative w-full h-[780px] rounded-[30px] overflow-hidden">
              <Image
                src="/verification-img.jpg"
                alt="Women training together"
                fill
                className="object-cover"
              />
            </div>

            {/* Right - Content */}
            <div className="space-y-6">
              <h2 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] leading-[56px]">
                Female Fitness Finder Verification Standards
              </h2>
              <div className="space-y-6 text-[#000000] text-[18px] leading-[36px] font-['Archivo',sans-serif] font-normal tracking-[3%]">
                <p>
                  At Female Fitness Finder, every space listed on our platform is carefully reviewed to ensure it meets our safety, privacy and is a women-first experience.
                </p>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <span className="text-[#8A7296] font-medium mt-1">•</span>
                    <span className="text-[#8A7296]">Our gym is simple.</span>
                  </li>
                </ul>
                <p>
                  To help women discover fitness environments where they feel comfortable, respected and supported.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Second Section */}
      <section className="bg-white relative">
        <div className="grid lg:grid-cols-2">
          {/* Left - Content */}
          <div className="pl-[215px] pr-[20px] flex items-center justify-center min-h-[780px]">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="w-[6px] h-[6px] bg-[#8A7296] rounded-full" />
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#8A7296] leading-[36px] tracking-[3%]">
                  Verified by Female Fitness Finder
                </p>
              </div>
              <h2 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] leading-[56px]">
                What "Verified by Female Fitness Finder" Means
              </h2>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                A "Verified by FFF" badge indicates that a fitness space has been independently reviewed and meets the following core criteria:
              </p>

              {/* Criteria Item 01 */}
              <div className="space-y-4">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#8A7296]">
                  01.
                </h3>
                <h4 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#8A7296]">
                  Women-Only Environment
                </h4>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  The space must provide a genuinely women-only environment.
                </p>
                <p className="font-['Archivo',sans-serif] text-[16px] font-bold text-[#000000] leading-[34px] tracking-[2%]">
                  This means:
                </p>
                <ul className="space-y-0">
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">
                      Access is restricted to women only during sessions or operating hours
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">
                      No mixed-gender training environments during listed sessions
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">
                      Clear policies in place to maintain a women-only space
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="relative w-full h-[780px]">
            <Image
              src="/verified-img.jpg"
              alt="Woman at gym"
              fill
              className="object-cover"
            />
          </div>
        </div>
      </section>

      {/* Third Section - Additional Criteria */}
      <section className="bg-[#f6efed] py-[100px]">
        <div className="max-w-[1280px] mx-auto px-[20px] space-y-[100px]">
          
          {/* Criteria 02 & 03 - Image Left, Content Right */}
          <div className="grid lg:grid-cols-2 gap-[100px] items-center">
            <div className="relative w-full h-[780px] rounded-[30px] overflow-hidden">
              <Image src="/criteria-02-03.jpg" alt="Women-led business" fill className="object-cover" />
            </div>
            <div className="space-y-8">
              {/* Criteria 02 */}
              <div className="space-y-4">
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#8A7296]">02.</h3>
              <h4 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#8A7296]">Women-Led Business or Instruction</h4>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">The business must be led by women.</p>
              <p className="font-['Archivo',sans-serif] text-[16px] font-bold text-[#000000] leading-[34px] tracking-[2%]">This includes:</p>
              <ul className="space-y-0">
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Female ownership, or</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Female-led coaching, classes, or instruction</span>
                </li>
              </ul>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">This ensures the environment is shaped with women's needs and experiences in mind.</p>
              </div>

              {/* Criteria 03 */}
              <div className="space-y-4">
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#8A7296]">03.</h3>
              <h4 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#8A7296]">Safety & Privacy Considerations</h4>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">The space must demonstrate awareness and consideration for women's safety and privacy.</p>
              <p className="font-['Archivo',sans-serif] text-[16px] font-bold text-[#000000] leading-[34px] tracking-[2%]">This may include:</p>
              <ul className="space-y-0">
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Appropriate changing facilities</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Transparent policies around CCTV (if applicable)</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">A layout and environment that prioritises comfort and discretion</span>
                </li>
              </ul>
              </div>
            </div>
          </div>

          {/* Criteria 04 & 05 - Content Left, Image Right */}
          <div className="grid lg:grid-cols-2 gap-[100px] items-center">
            <div className="space-y-8">
              {/* Criteria 04 */}
              <div className="space-y-4">
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#8A7296]">04.</h3>
              <h4 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#8A7296]">Legitimate & Active Business</h4>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">All listings must be genuine, active businesses.</p>
              <p className="font-['Archivo',sans-serif] text-[16px] font-bold text-[#000000] leading-[34px] tracking-[2%]">This includes:</p>
              <ul className="space-y-0">
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Clear services offered</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Accurate contact information</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">A visible online presence or verifiable location</span>
                </li>
              </ul>
              </div>

              {/* Criteria 05 */}
              <div className="space-y-4">
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#8A7296]">05.</h3>
              <h4 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#8A7296]">Alignment with Women-First Values</h4>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">We prioritise spaces that create a respectful, inclusive and supportive environment for women.</p>
              <p className="font-['Archivo',sans-serif] text-[16px] font-bold text-[#000000] leading-[34px] tracking-[2%]">This includes:</p>
              <ul className="space-y-0">
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">A welcoming atmosphere</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">Respect for different comfort levels, backgrounds and needs</span>
                </li>
                <li className="flex gap-3">
                  <svg className="w-5 h-5 text-[#8A7296] flex-shrink-0 mt-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/>
                  </svg>
                  <span className="font-['Archivo',sans-serif] text-[16px] font-normal text-[#000000] leading-[34px] tracking-[2%]">A focus on wellbeing, not just performance</span>
                </li>
              </ul>
              </div>
            </div>
            <div className="relative w-full h-[780px] rounded-[30px] overflow-hidden">
              <Image src="/criteria-04-05.jpg" alt="Women-first values" fill className="object-cover" />
            </div>
          </div>

        </div>
      </section>

      {/* Final Section - Our Approach & Ongoing Review */}
      <section className="bg-white relative">
        <div className="grid lg:grid-cols-2 gap-[80px]">
          {/* Left - Image */}
          <div className="relative w-full h-[780px]">
            <Image
              src="/our-approach.jpg"
              alt="Our approach"
              fill
              className="object-cover"
            />
          </div>

          {/* Right - Content */}
          <div className="pl-[20px] pr-[215px] flex items-center justify-center min-h-[780px]">
            <div className="space-y-8">
              {/* Our Approach */}
              <div className="space-y-4">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A]">Our Approach</h3>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  We take a curated approach to listings on Female Fitness Finder.
                </p>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  Not every business will be listed - only those that align with our standards.
                </p>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  This ensures that when you use our platform, you can trust that every space has been reviewed with care.
                </p>
              </div>

              {/* Ongoing Review */}
              <div className="space-y-4">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A]">Ongoing Review</h3>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  Verification is not a one-time process.
                </p>
                <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#000000] leading-[36px] tracking-[3%]">
                  We may periodically review listings to ensure standards are maintained and remove listings that no longer meet our criteria.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Questions or Feedback Section */}
      <section className="bg-[#4C3E58] py-[60px]">
        <div className="max-w-[1280px] mx-auto px-[20px] text-center">
          <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-white mb-6">
            Questions or Feedback
          </h2>
          <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-white leading-[36px] tracking-[0%] mb-8">
            If you have questions about our standards or would like to suggest a space for review, please contact:
          </p>
          <a 
            href="mailto:feedback@femalefitnessfinder.com" 
            className="inline-flex items-center gap-3 bg-[#8A7296] hover:bg-[#7a647d] transition-colors px-[38px] py-[24px] rounded-[10px]"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            <span className="font-['Archivo',sans-serif] text-[18px] font-normal text-white">
              feedback@femalefitnessfinder.com
            </span>
          </a>
        </div>
      </section>

      <HomepageFooter />
    </div>
  );
}
